import { useEffect, useState, useMemo, useCallback } from "react";
import { api, formatErr } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Plus, Pencil, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Comments from "../components/Comments";

const DRAWING_TYPES = [
  { key: "layout", label: "Layout" },
  { key: "av_cabinet", label: "AV Cabinet" },
  { key: "acoustic_layout", label: "Acoustic Layout" },
  { key: "seating_layout", label: "Seating Layout" },
  { key: "electrical", label: "Electrical" },
  { key: "false_ceiling", label: "False Ceiling" },
  { key: "civil_changes", label: "Civil Changes" },
  { key: "3d_view", label: "3D View" },
];

const DRAWING_STATUSES = [
  { key: "yet_to_start", label: "Yet to Start", color: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
  { key: "in_progress", label: "In Progress", color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  { key: "internal_review", label: "Internal Review", color: "bg-indigo-500/20 text-indigo-300 border-indigo-500/30" },
  { key: "sent_to_client", label: "Sent to Client", color: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30" },
  { key: "revision_required", label: "Revision Required", color: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  { key: "approved", label: "Approved", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  { key: "released_for_execution", label: "Released for Execution", color: "bg-primary/20 text-primary border-primary/30" },
];

const empty = {
  project_id: "", drawing_type: "layout", assigned_designer_id: "",
  status: "yet_to_start", revision: 0, due_date: "", approval_date: "",
  released_for_execution: false, notes: "",
};

export default function Design() {
  const [drawings, setDrawings] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ project_id: "", status: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const { data } = await api.get("/drawings", { params });
    setDrawings(data);
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/users").then((r) => setUsers(r.data));
  }, []);

  const designers = useMemo(() => users.filter((u) =>
    (u.roles || [u.role]).some((r) => ["design", "designer"].includes(r))
  ), [users]);

  const projectName = (id) => projects.find((p) => p.id === id)?.client_name || "—";
  const designerName = (id) => users.find((u) => u.id === id)?.name || "—";

  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (d) => {
    setEditId(d.id);
    setForm({
      project_id: d.project_id, drawing_type: d.drawing_type,
      assigned_designer_id: d.assigned_designer_id || "",
      status: d.status || "yet_to_start", revision: d.revision || 0,
      due_date: d.due_date || "", approval_date: d.approval_date || "",
      released_for_execution: !!d.released_for_execution,
      notes: d.notes || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.project_id) { toast.error("Project is required"); return; }
    try {
      if (editId) {
        await api.patch(`/drawings/${editId}`, form);
      } else {
        await api.post("/drawings", form);
      }
      toast.success(editId ? "Drawing updated" : "Drawing added");
      setOpen(false); load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Save failed");
    }
  };

  const pending = drawings.filter((d) => !["approved", "released_for_execution"].includes(d.status));
  const revision = drawings.filter((d) => d.status === "revision_required");
  const approved = drawings.filter((d) => d.status === "approved" || d.status === "released_for_execution");

  return (
    <div className="space-y-4" data-testid="design-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Design Studio</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Drawings & Design</h1>
        </div>
        <Button onClick={openNew} data-testid="new-drawing-btn"><Plus className="h-4 w-4 mr-2" />New Drawing</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total" value={drawings.length} tone="muted" />
        <Stat label="Pending" value={pending.length} tone="warning" />
        <Stat label="Revision required" value={revision.length} tone="danger" />
        <Stat label="Approved / Released" value={approved.length} tone="success" />
      </div>

      <Card className="p-4 bg-card border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select value={filters.project_id || "all"} onValueChange={(v) => setFilters({ ...filters, project_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="design-filter-project"><SelectValue placeholder="Project" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All projects</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.client_name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{DRAWING_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Project</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Designer</TableHead>
            <TableHead>Rev</TableHead>
            <TableHead>Due</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Released</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {drawings.map((d) => {
              const s = DRAWING_STATUSES.find((x) => x.key === d.status);
              return (
                <TableRow key={d.id} data-testid={`drawing-row-${d.id}`}>
                  <TableCell className="font-medium">{projectName(d.project_id)}</TableCell>
                  <TableCell className="text-xs capitalize">{d.drawing_type?.replace(/_/g, " ")}</TableCell>
                  <TableCell className="text-xs">{designerName(d.assigned_designer_id)}</TableCell>
                  <TableCell className="font-mono text-xs">R{d.revision || 0}</TableCell>
                  <TableCell className="font-mono text-xs">{d.due_date || "—"}</TableCell>
                  <TableCell><Badge className={`${s?.color} border`}>{s?.label}</Badge></TableCell>
                  <TableCell>{d.released_for_execution ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell><Button variant="ghost" size="icon" onClick={() => openEdit(d)}><Pencil className="h-3.5 w-3.5" /></Button></TableCell>
                </TableRow>
              );
            })}
            {drawings.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No drawings yet. Create one to start tracking design deliverables.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border" data-testid="drawing-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Drawing" : "New Drawing"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <F label="Project" required>
              <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="drawing-project"><SelectValue placeholder="Choose project" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.client_name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Drawing Type" required>
              <Select value={form.drawing_type} onValueChange={(v) => setForm({ ...form, drawing_type: v })}>
                <SelectTrigger data-testid="drawing-type"><SelectValue /></SelectTrigger>
                <SelectContent>{DRAWING_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Assigned Designer">
              <Select value={form.assigned_designer_id || "none"} onValueChange={(v) => setForm({ ...form, assigned_designer_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Assign" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Unassigned</SelectItem>{(designers.length ? designers : users).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="drawing-status"><SelectValue /></SelectTrigger>
                <SelectContent>{DRAWING_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Revision Number">
              <Input type="number" min="0" value={form.revision} onChange={(e) => setForm({ ...form, revision: Number(e.target.value) })} />
            </F>
            <F label="Due Date">
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </F>
            <F label="Approval Date">
              <Input type="date" value={form.approval_date} onChange={(e) => setForm({ ...form, approval_date: e.target.value })} />
            </F>
            <F label="Release for Execution">
              <label className="flex items-center gap-2 h-10">
                <input type="checkbox" checked={!!form.released_for_execution} onChange={(e) => setForm({ ...form, released_for_execution: e.target.checked })} data-testid="drawing-release" />
                <span className="text-sm">Ready to execute on site</span>
              </label>
            </F>
            <div className="col-span-2">
              <F label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></F>
            </div>
          </div>
          {editId && (
            <div className="pt-3 border-t border-border">
              <Comments entity="drawing" parentId={editId} />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="drawing-save-btn">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, tone = "muted" }) {
  const tones = {
    muted: "text-foreground",
    warning: "text-amber-400",
    danger: "text-red-400",
    success: "text-emerald-400",
  };
  return (
    <Card className="p-3 bg-card border-border">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-mono text-2xl mt-0.5 ${tones[tone]}`}>{value}</div>
    </Card>
  );
}

function F({ label, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
